import React, { useState, useEffect } from "react";
import { useHistory, useParams, useLocation } from "react-router";
import {
  useOrganizationDetails,
  useOrganizationUserIndex,
  useOrganizationUser,
} from "hooks/api/fulfillment/organization";
import useUser from "../../../../hooks/reduxContainer/_common/useUser";
import { useFeatureFlagQuery } from "../../../../hooks/useFeatureFlagQuery";
import SearchBox from "components/fulfillment/_common/SearchBox";
import UsersTable from "./UsersTable";
import { UserAddOutlined, SettingOutlined, DownOutlined, MailOutlined, FilterOutlined } from "@ant-design/icons";
import { Alert, Button, Space, Switch, Typography, Dropdown, Menu, Modal, notification, Checkbox } from "antd";
import { get } from "lodash-es";
import { useMutation, useQuery } from "react-query";
import { batchUpdateUsers, batchPasswordReset } from "../../../../api/accounts/user";
import { fetchUserRoles } from "../../../../api/accounts/roles";
import User from "../../../../lib/regular/user";
import Organization from "../../../../lib/organization";
import { OPS_ONLY } from "config/roleConfig";

const SEARCH_DELAY_IN_MS = 200;
const PAGE_SIZE = 15;

const OrganizationUsersIndexPage = () => {
  const history = useHistory();
  const params = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const cachedSearch = searchParams.get("search") || "";
  const cachedPage = +(searchParams.get("page") || 1);

  const [search, setSearch] = useState(cachedSearch);
  const [page, setPage] = useState(cachedPage);
  const [timer, setTimer] = useState(null);
  const [showActive, setShowActive] = useState(true);

  // Filter state
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);

  // Batch selection state
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [areAllUsersSelected, setAreAllUsersSelected] = useState(false);
  const [excludedUsers, setExcludedUsers] = useState([]);

  const { organizationDetails, fetchOrganizationDetails } = useOrganizationDetails();
  const { organizationUserIndex, fetchOrganizationUserIndex } = useOrganizationUserIndex();
  const { createOrganizationUser } = useOrganizationUser();

  const organizationID = get(params, "organization_id", "");

  const isGoBolt = Organization.isGoBolt(organizationDetails.data?.name);

  const { user } = useUser();
  const enableCreateUser = useFeatureFlagQuery("enable-create-user", {
    user_id: user.id,
    organization_id: organizationID,
  });

  const handleCreateTempUser = async () => {
    const email = Math.random().toString(36).slice(2, 10) + "@gobolt.com";
    const password = Math.random().toString(36).slice(2, 8);
    const body = {
      user: {
        email,
        password,
        first_name: "Temporary",
        last_name: "User",
        role_id: "6f31f596-4804-11ee-b5f2-c3ae39928bcd", // TEMP role
        role: "is_admin",
        subrole: "operation_specialist",
        organization_id: organizationID,
      },
    };

    const user = await createOrganizationUser(body);
    if (user?.id) {
      Modal.success({
        title: "Temporary User Created",
        content: (
          <Space direction="vertical">
            <Typography.Text>
              User:&nbsp;
              <Typography.Text strong copyable>
                {user.email}
              </Typography.Text>
            </Typography.Text>
            <Typography.Text>
              Password:&nbsp;
              <Typography.Text strong copyable>
                {password}
              </Typography.Text>
            </Typography.Text>
          </Space>
        ),
      });
      fetchOrganizationUsers();
    }
  };

  // Fetch organization roles for the dropdown
  const { data: organizationRoles = [] } = useQuery(
    ["userRoles", organizationID],
    () => fetchUserRoles({ organization_id: organizationID }),
    { enabled: !!organizationID }
  );

  // Create array of current page users for batch selection
  const currentPageUsers = organizationUserIndex?.data?.map((user) => user.id) || [];

  // Batch update mutation
  const { mutate: doBatchUpdateUsers, isLoading: isBatchUpdating } = useMutation(batchUpdateUsers, {
    onSuccess: (data) => {
      notification.success({
        message: `${data.users.length} user${data.users.length > 1 ? "s" : ""} updated successfully`,
      });
      // Clear selections after successful batch update
      setSelectedUsers([]);
      setAreAllUsersSelected(false);
      setExcludedUsers([]);
      // Use fetchOrganizationUsers() to respect current filter state
      fetchOrganizationUsers();
    },
    onError: () => {
      notification.error({
        message: "Batch update failed",
        description: "Please try again or contact support.",
      });
    },
  });

  // Batch password reset mutation
  const { mutate: doBatchPasswordReset, isLoading: isPasswordResetting } = useMutation(batchPasswordReset, {
    onSuccess: (data) => {
      // Check if there are any failed user IDs
      if (data.failed_user_ids && data.failed_user_ids.length > 0) {
        // Map failed user IDs to their emails for more actionable feedback
        const failedUserEmails = data.failed_user_ids
          .map((failedId) => {
            const failedUser = organizationUserIndex?.data?.find((user) => user.id === failedId);
            return failedUser?.email || `User ID: ${failedId}`;
          })
          .filter(Boolean);

        notification.error({
          message: "Password reset partially failed",
          description: (
            <div>
              <p>Failed to send password reset emails to:</p>
              <ul style={{ marginBottom: 0 }}>
                {failedUserEmails.map((email, index) => (
                  <li key={index}>{email}</li>
                ))}
              </ul>
            </div>
          ),
        });
        return;
      }

      // Success case - use data.length since API returns array directly
      const userCount = Array.isArray(data) ? data.length : data.users ? data.users.length : 0;
      notification.success({
        message: `Password reset email sent to ${userCount} user${userCount > 1 ? "s" : ""}`,
      });
      // Clear selections after successful password reset
      setSelectedUsers([]);
      setAreAllUsersSelected(false);
      setExcludedUsers([]);
    },
    onError: () => {
      notification.error({
        message: "Password reset failed",
        description: "Please try again or contact support.",
      });
    },
  });

  // Handle updating selected users when "All" is selected and data changes
  useEffect(() => {
    if (areAllUsersSelected && organizationUserIndex.data) {
      // When "All" is selected, we only need to track excluded users
      // The actual selection will be calculated in getSelectedUserCount
      setExcludedUsers([]);
    }
  }, [organizationUserIndex.data, areAllUsersSelected]);

  useEffect(() => {}, [selectedUsers, areAllUsersSelected]);

  const fetchOrganizationUsers = () => {
    fetchOrganizationUserIndex(getQueryOptions());
  };

  // Handle role filter changes - fetch when filters are cleared
  useEffect(() => {
    // Clear selected users when role filter changes
    setSelectedUsers([]);
    setAreAllUsersSelected(false);
    setExcludedUsers([]);

    if (selectedRoleIds.length === 0) {
      fetchOrganizationUsers();
    }
  }, [selectedRoleIds]);

  const getQueryOptions = () => {
    const trimmedSearchInput = search ? search.trim() : "";
    let queryOption = {};
    if (organizationID) queryOption.organization_id = organizationID;
    if (trimmedSearchInput) queryOption.q = trimmedSearchInput;
    if (page) queryOption.page = page;
    queryOption.per_page = PAGE_SIZE;
    queryOption.disabled = !showActive;
    if (selectedRoleIds.length > 0) {
      queryOption.role_ids = selectedRoleIds;
    }
    return queryOption;
  };

  const onFetchRequested = () => {
    if (timer) clearTimeout(timer);
    const newTimer = setTimeout(() => fetchOrganizationUsers(), SEARCH_DELAY_IN_MS);
    setTimer(newTimer);
  };

  const checkEmpty = (name, value) => {
    if (name === "page") return true;
    if (name === "search") return !!value;
  };

  const updateSearchParams = (name, value) => {
    const valueExists = checkEmpty(name, value);
    if (valueExists) searchParams.set(name, value);
    else searchParams.delete(name);
  };

  const updateUrlAndFetchData = () => {
    updateSearchParams("page", page);
    updateSearchParams("search", search);
    const url = searchParams.toString();
    history.replace(`?${url}`);
    onFetchRequested();
  };

  // eslint-disable-next-line
  /* eslint-disable react-hooks/exhaustive-deps */
  useEffect(() => {
    updateUrlAndFetchData();
  }, [page, search, showActive]);

  useEffect(() => {
    if (organizationID) {
      fetchOrganizationDetails(organizationID);
    }
    window.scrollTo(0, 0);
  }, []);

  if (!organizationUserIndex.data && organizationUserIndex.error)
    return <Alert message={organizationUserIndex.error} type="error" banner />;

  const onCreateUserClick = () => {
    history.push(`/organizations/${organizationID}/users/create`);
  };

  const getSelectedUserCount = () => {
    if (areAllUsersSelected) {
      const count = (organizationUserIndex?.data?.length || 0) - excludedUsers.length;
      return count;
    }
    return selectedUsers.length;
  };

  const handleBatchAction = (actionKey) => {
    const selectedCount = getSelectedUserCount();

    // Parse the action key to determine field and value
    const [field, value] = actionKey.split(".");

    let actionText = "";
    let updateParams = {};

    switch (field) {
      case "disabled":
        actionText = value === "true" ? "disable" : "enable";
        updateParams.disabled = value === "true";
        break;
      case "subrole":
        const subroleName = User.getSubroleName(value);
        actionText = `set subrole to "${subroleName}"`;
        updateParams.subrole = value;
        break;
      case "role_id":
        const roleName = organizationRoles.find((role) => role.id === value)?.name || "Unknown Role";
        actionText = `set organization role to "${roleName}"`;
        updateParams.role_id = value;
        break;
      default:
        notification.error({
          message: "Invalid action",
          description: "Unknown batch action type.",
        });
        return;
    }

    Modal.confirm({
      title: `Confirm ${actionText}`,
      content: (
        <div>
          <p>
            Are you sure you want to {actionText} for {selectedCount} user{selectedCount > 1 ? "s" : ""}?
          </p>
        </div>
      ),
      okText: `Update Users`,
      okButtonProps: {
        loading: isBatchUpdating,
      },
      cancelText: "Cancel",
      onOk: () => {
        confirmBatchAction(updateParams);
      },
    });
  };

  const confirmBatchAction = (updateParams) => {
    let userIDsToUpdate = [];

    if (areAllUsersSelected) {
      // If all users are selected, get all user IDs except excluded ones
      userIDsToUpdate = (organizationUserIndex?.data || [])
        .filter((user) => !excludedUsers.includes(user.id))
        .map((user) => user.id);
    } else {
      // If specific users are selected, use their IDs directly
      userIDsToUpdate = selectedUsers;
    }

    if (userIDsToUpdate.length === 0) {
      notification.warning({
        message: "No users selected",
        description: "Please select users to perform batch action.",
      });
      return;
    }

    doBatchUpdateUsers({
      userIDs: userIDsToUpdate,
      ...updateParams,
    });
  };

  const handlePasswordReset = () => {
    let userIDsToReset = [];

    if (areAllUsersSelected) {
      // If all users are selected, get all user IDs except excluded ones
      userIDsToReset = (organizationUserIndex?.data || [])
        .filter((user) => !excludedUsers.includes(user.id))
        .map((user) => user.id);
    } else {
      // If specific users are selected, use their IDs directly
      userIDsToReset = selectedUsers;
    }

    if (userIDsToReset.length === 0) {
      notification.warning({
        message: "No users selected",
        description: "Please select users to send password reset emails.",
      });
      return;
    }

    Modal.confirm({
      title: "Request Password Reset",
      content: `Are you sure you want to send password reset emails to ${userIDsToReset.length} user${
        userIDsToReset.length > 1 ? "s" : ""
      }?`,
      okText: "Send Reset Emails",
      cancelText: "Cancel",
      onOk: () => {
        doBatchPasswordReset({
          userIDs: userIDsToReset,
        });
      },
    });
  };

  const renderMarkSelectedAsDropdown = () => {
    const selectedCount = getSelectedUserCount();
    const hasNoSelectedUsers = selectedCount <= 0;
    const isLoading = isBatchUpdating;

    const menu = (
      <Menu onClick={({ key }) => handleBatchAction(key)}>
        <Menu.SubMenu key="disabled" title="Status">
          <Menu.Item key="disabled.false">Enable</Menu.Item>
          <Menu.Item key="disabled.true" danger>
            Disable
          </Menu.Item>
        </Menu.SubMenu>

        <Menu.SubMenu key="role_id" title="Role">
          {organizationRoles.map((role) => (
            <Menu.Item key={`role_id.${role.id}`}>{role.name}</Menu.Item>
          ))}
        </Menu.SubMenu>

        <Menu.SubMenu key="subrole" title="Subrole">
          {User.getSubroles().map((subrole) => (
            <Menu.Item key={`subrole.${subrole.value}`}>{subrole.name}</Menu.Item>
          ))}
        </Menu.SubMenu>
      </Menu>
    );

    const tooltipTitle = hasNoSelectedUsers
      ? "Please select at least one user"
      : `Update ${selectedCount} selected user${selectedCount > 1 ? "s" : ""}`;

    return (
      <Dropdown overlay={menu} disabled={hasNoSelectedUsers || isLoading} trigger={["click"]}>
        <Button loading={isLoading} disabled={hasNoSelectedUsers} title={tooltipTitle}>
          Mark Selected As <DownOutlined />
        </Button>
      </Dropdown>
    );
  };

  const renderRoleFilter = () => {
    const handleRoleChange = (roleId) => {
      setSelectedRoleIds((prev) => {
        if (prev.includes(roleId)) {
          return prev.filter((id) => id !== roleId);
        } else {
          return [...prev, roleId];
        }
      });
      if (page !== 1) setPage(1);
    };

    const handleClearFilters = () => {
      setSelectedRoleIds([]);
      if (page !== 1) setPage(1);
    };

    const handleDropdownVisibleChange = (visible) => {
      // Only fetch when dropdown is closed (visible becomes false)
      if (!visible) {
        fetchOrganizationUsers();
      }
    };

    const menu = (
      <Menu style={{ maxHeight: "300px", overflowY: "auto" }}>
        {organizationRoles.map((role) => (
          <Menu.Item key={role.id} onClick={(e) => e.stopPropagation()}>
            <Checkbox
              style={{ width: "100%" }}
              checked={selectedRoleIds.includes(role.id)}
              onClick={() => handleRoleChange(role.id)}
            >
              {role.name}
            </Checkbox>
          </Menu.Item>
        ))}
        {selectedRoleIds.length > 0 && (
          <>
            <Menu.Divider />
            <Menu.Item key="clear-filters" onClick={() => handleClearFilters()}>
              <Button
                type="link"
                size="small"
                // onClick={handleClearFilters}
                style={{ padding: 0, height: "auto" }}
              >
                Clear
              </Button>
            </Menu.Item>
          </>
        )}
      </Menu>
    );

    const hasFilters = selectedRoleIds.length > 0;
    const buttonText = hasFilters ? `Roles (${selectedRoleIds.length})` : "All Roles";

    return (
      <Dropdown overlay={menu} trigger={["click"]} placement="bottomLeft" onVisibleChange={handleDropdownVisibleChange}>
        <Button icon={<FilterOutlined />} type={hasFilters ? "primary" : "default"}>
          {buttonText} <DownOutlined />
        </Button>
      </Dropdown>
    );
  };

  const renderTopButtons = () => {
    if (!organizationID) return null;
    const selectedCount = getSelectedUserCount();
    const hasSelectedUsers = selectedCount > 0;

    return (
      <Space>
        {renderMarkSelectedAsDropdown()}
        {hasSelectedUsers && (
          <Button
            onClick={handlePasswordReset}
            loading={isPasswordResetting}
            key="password-reset"
            icon={<MailOutlined />}
          >
            Password Reset
          </Button>
        )}
        {user.subrole && OPS_ONLY.includes(user.subrole) ? null : (
          <Button
            onClick={() => history.push(`/organizations/${organizationID}/roles`)}
            key="user-roles"
            icon={<SettingOutlined />}
          >
            Role Management
          </Button>
        )}
        {enableCreateUser.data?.state && (
          <>
            <Button
              id="create-user-button"
              className="add-user-btn"
              icon={<UserAddOutlined />}
              onClick={onCreateUserClick}
            >
              Add User
            </Button>
            {isGoBolt && (
              <Button
                id="create-user-button"
                className="add-user-btn"
                icon={<UserAddOutlined />}
                onClick={handleCreateTempUser}
                isLoading={organizationDetails.loading}
                disabled={organizationDetails.loading}
              >
                Add Temp User
              </Button>
            )}
          </>
        )}
      </Space>
    );
  };

  const buildUserHeader = () => {
    return (
      <div className="users-header">
        <div className="title-with-search">
          <Typography.Title level={4}>Users</Typography.Title>
        </div>
        <div className="customer-specific-options">
          <Space>
            <SearchBox
              value={search}
              onChange={(value) => {
                setSearch(value);
                if (page !== 1) setPage(1);
              }}
              placeholder="Search for users"
              width={300}
            />
            <Switch
              checked={showActive}
              checkedChildren="Active"
              unCheckedChildren="Archived"
              onClick={setShowActive}
            />
          </Space>
          {renderTopButtons()}
        </div>
        <div style={{ marginTop: "16px" }}>
          <Space>{renderRoleFilter()}</Space>
        </div>
      </div>
    );
  };

  return (
    <div className="page-container" id="organization-users-index-page">
      <div className="organization-users">
        {buildUserHeader()}
        <br />
        {organizationUserIndex.error && <Alert message={organizationUserIndex.error} type="error" banner />}
        <UsersTable
          page={page}
          setPage={setPage}
          pageSize={PAGE_SIZE}
          isLoading={organizationUserIndex.loading}
          data={organizationUserIndex.data || []}
          organizationID={organizationID}
          onReFetchUsers={fetchOrganizationUsers}
          showActive={showActive}
          // Batch selection props
          selectedUsers={selectedUsers}
          setSelectedUsers={setSelectedUsers}
          areAllUsersSelected={areAllUsersSelected}
          setAreAllUsersSelected={setAreAllUsersSelected}
          excludedUsers={excludedUsers}
          setExcludedUsers={setExcludedUsers}
          currentPageUsers={currentPageUsers}
          isBatchActionMode={true}
        />
      </div>
    </div>
  );
};

export default OrganizationUsersIndexPage;
